import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../../services/apiServices";

// API base URL
const API_URL = "companies";

// Async Thunks for CRUD operations
export const fetchCompanies = createAsyncThunk(
  "company/fetchCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(API_URL);
      return response.data; // Assuming `response.data` contains the companies array
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching companies");
    }
  }
);

export const fetchCompanyProfile = createAsyncThunk(
  "company/fetchCompanyProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`${API_URL}/${id}/profile`);
      return response.data; // Assuming `response.data` contains the profile object
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching company profile");
    }
  }
);

export const createCompany = createAsyncThunk(
  "company/createCompany",
  async (companyData, { rejectWithValue }) => {
    try {
      const response = await apiServices.post(API_URL, companyData);
      return response.data; // Assuming `response.data` contains the created company object
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating company");
    }
  }
);

export const updateCompany = createAsyncThunk(
  "company/updateCompany",
  async ({ id, companyData }, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`${API_URL}/${id}`, companyData);
      return response.data; // Assuming `response.data` contains the updated company object
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating company");
    }
  }
);

export const deleteCompany = createAsyncThunk(
  "company/deleteCompany",
  async (id, { rejectWithValue }) => {
    try {
      await apiServices.delete(`${API_URL}/${id}`);
      return { id }; // Returning only the ID of the deleted company
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting company");
    }
  }
);

// Initial State
const initialState = {
  companies: [], // List of companies
  profile: null, // Single company profile
  loading: false,
  error: null,
};

// Company Slice
const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {}, // Synchronous reducers if needed
  extraReducers: (builder) => {
    builder
      // Fetch Companies
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch companies";
      })

      // Fetch Company Profile
      .addCase(fetchCompanyProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload || null;
      })
      .addCase(fetchCompanyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch company profile";
      })

      // Create Company
      .addCase(createCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies.push(action.payload); // Assuming `payload` contains the created company
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create company";
      })

      // Update Company
      .addCase(updateCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCompany = action.payload;
        const index = state.companies.findIndex((c) => c._id === updatedCompany._id);
        if (index !== -1) state.companies[index] = updatedCompany;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update company";
      })

      // Delete Company
      .addCase(deleteCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.filter((c) => c._id !== action.payload.id);
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete company";
      });
  },
});

export default companySlice.reducer;
